/**
 * Derive the post-upload processing banner from the live `client_processing_queue`
 * docs the Upload page already subscribes to (the "Uploaded Files" list).
 *
 * Why this exists (2026-06): the old banner went GREEN the instant the Storage PUT
 * succeeded — BEFORE any processing. A file that then failed validation (e.g. wrong
 * template → 0 records) showed a green "uploaded successfully" while the queue doc
 * was written `status:'failed'` and never read back. The banner lied.
 *
 * This is a pure reducer over (activeUploadId, queueDocs, timedOut): the banner goes
 * GREEN only on `status:'completed' && recordCount>0`, RED on `failed`, amber on
 * duplicate / zero-record completion, and stays neutral "processing…" until a terminal
 * state — with a timeout fallback for the limbo case where Stage 1 throws before the
 * queue doc is ever created.
 */

export type UploadBannerKind =
  | 'processing'
  | 'success'
  | 'duplicate'
  | 'warning'
  | 'failed'
  | 'timeout';

export interface UploadProcessingBanner {
  kind: UploadBannerKind;
  text: string;
}

/** Minimal shape we read off a `client_processing_queue` doc. */
export interface QueueDocLike {
  uploadId?: string;
  status?: string;
  recordCount?: number;
  isDuplicate?: boolean;
  error?: string;
  displayName?: string;
  fileName?: string;
}

const TERMINAL_FAILED = new Set(['failed', 'failed_orphaned']);

/**
 * @param activeUploadId  the uploadId returned by issueSignedUploadUrl for the in-flight
 *                        upload, or null when nothing is being tracked.
 * @param queueDocs       live company queue docs (the page's `uploadedFiles`).
 * @param timedOut        true once the limbo timeout has elapsed without a terminal state.
 * @param fallbackName    display name to use before the queue doc appears.
 * @returns the banner to render, or null when nothing should show.
 */
export function deriveUploadProcessingBanner(
  activeUploadId: string | null,
  queueDocs: QueueDocLike[],
  timedOut: boolean,
  fallbackName?: string
): UploadProcessingBanner | null {
  if (!activeUploadId) return null;

  const doc = (queueDocs || []).find((d) => d.uploadId === activeUploadId);
  const name = doc?.displayName || doc?.fileName || fallbackName || 'Your file';
  const status = doc?.status;

  // Terminal: failure — fail LOUD with the backend's reason.
  if (status && TERMINAL_FAILED.has(status)) {
    return {
      kind: 'failed',
      text: doc?.error
        ? `Upload failed: ${doc.error}`
        : `"${name}" failed to process — no records were added. Check the file matches the selected loan book.`,
    };
  }

  // Terminal: completed.
  if (status === 'completed') {
    if (doc?.isDuplicate) {
      return {
        kind: 'duplicate',
        text: `"${name}" was recognised as a duplicate of an earlier upload — no new records were added.`,
      };
    }
    const rc = doc?.recordCount ?? 0;
    if (rc > 0) {
      return {
        kind: 'success',
        text: `✓ "${name}" processed successfully — ${rc.toLocaleString()} record${rc === 1 ? '' : 's'} added.`,
      };
    }
    // Completed with zero records and not a duplicate — almost always a wrong-file/template.
    return {
      kind: 'warning',
      text: `"${name}" completed but added 0 records — check the file matches the selected loan book.`,
    };
  }

  // Non-terminal (pending/processing/reserved, or the queue doc hasn't appeared yet).
  if (timedOut) {
    return {
      kind: 'timeout',
      text: `"${name}" is still processing — check the Uploaded Files list below for the final status.`,
    };
  }
  return { kind: 'processing', text: `"${name}" uploaded — processing…` };
}

/** Tailwind classes per banner kind. Neutral = blue; success = green; soft-fail = amber; fail = red. */
export function uploadBannerClass(kind: UploadBannerKind): string {
  switch (kind) {
    case 'success':
      return 'bg-green-900 text-green-300 p-3 rounded mb-4';
    case 'failed':
      return 'bg-red-900 text-red-300 p-3 rounded mb-4';
    case 'duplicate':
    case 'warning':
      return 'bg-amber-900 text-amber-300 p-3 rounded mb-4';
    case 'processing':
    case 'timeout':
    default:
      return 'bg-blue-900 text-blue-300 p-3 rounded mb-4';
  }
}
